import Parse from 'parse'; // TODO: use global authentication once implemented

import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { ComponentStore } from '@ngrx/component-store';
import { getRouterSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Analyst, IState } from '@analysis/domain';
import { BehaviorSubject, Subject, combineLatest, merge, of, Observable } from 'rxjs';

import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  AnalystStore,
  AssetStore,
  ViewContextStore,
  NavigationStore,
  RoiStore,
  FilterConfigStore,
  AnalysisStateStore,
  RoiChangeStore,
  MosaicGalleryPanels,
  RoiStatsStore,
  CounterExclusionMode,
} from '../../stores';
import {
  SampleViewLoadingEffects,
  BreadcrumbNavigationEffects,
  FilterConfigEffects,
  RoiRestoreEffects,
  RoiLabelingEffects,
  RoiAnalysisReviewEffects,
  RoiSyncEffects,
  RoiStatsEffects,
  RoiRevertEffects,
} from '../../effects';
import { StatsMode } from '../stats-settings/stats-settings.component';
import { ProtocolStore } from '@telespot/protocols/ui';
import {
  ConfirmationDialogComponent,
  NavigationBreadcrumbIndicator,
  NavigationBreadcrumbLevel,
  NavigationBreadcrumbOption,
  SnackbarService,
} from '@shared/ui';
import { ApiAuthErrors } from '@telespot/web-core';
import { SampleAiAnalysisProgressStore, algorithmAiAnalysisLocked } from '../microscope-image/state';

/** `desiredUrlHistoryId$` marker: the selection is the current user, so the URL carries no historyId. */
const SELECTION_IS_SELF = 'self';

/**
 * TODO:
 * - clean up
 * - split into features
 * - test
 */
@Injectable()
export class MosaicDashboardFacade extends ComponentStore<Record<string, never>> {
  private readonly saveBlockedByAiAnalysisSubject = new Subject<void>();
  public readonly saveBlockedByAiAnalysis$ = this.saveBlockedByAiAnalysisSubject.asObservable();

  private readonly ALERT_DEBOUNCE_TIME = 500;
  public hasUnsyncedChanges = false;

  public readonly sampleId$ = this.globalStore.select(getRouterSelectors().selectRouteParam('sampleId')) as Observable<string>;
  public readonly caseId$ = this.globalStore.select(getRouterSelectors().selectRouteParam('caseid')) as Observable<string>;
  public readonly historyId$ = this.globalStore.select(getRouterSelectors().selectQueryParam('historyId')) as Observable<string>;

  private readonly routerIdle$ = this.router.events.pipe(
    filter(
      (e) =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError
    ),
    map((e) => !(e instanceof NavigationStart)),
    startWith(true),
    distinctUntilChanged(),
    // refCount MUST stay false: `wireAnalystUrlSync` subscribes with `take(1)`, so a
    // ref-counted share would tear the buffer down after every use and replay
    // `startWith(true)` on the next subscribe — reporting "idle" in the middle of a
    // navigation.
    shareReplay({ bufferSize: 1, refCount: false })
  );

  public readonly offlineMode$ = of(false);

  public readonly labels$ = this.protocolStore.options$;
  public readonly categories$ = this.protocolStore.taggingTasks$;
  public readonly pipelines$ = this.protocolStore.taggingPipelines$;
  public readonly protocolLoading$ = this.protocolStore.loading$;
  public readonly currentUser$ = of(Parse.User.current() as Parse.User | undefined);
  public readonly selectedAnalyst$ = this.analystStore.selectedAnalyst$;
  public readonly selectedAnalystIsCurrentUser$ = this.analystStore.selectedAnalystIsCurrentUser$;
  public readonly analysts$ = this.analystStore.analystsExcludingCurrentUser$;

  /**
   * What the URL's `historyId` should be for the current selection: the analyst's
   * historyId, {@link SELECTION_IS_SELF} when it is the current user (no param), or
   * `null` while nothing is resolved yet.
   *
   * Projected in ONE debounced selector on purpose. `selectedAnalystHistoryId$` and
   * `selectedAnalystIsCurrentUser$` are separate views of the same state, so pairing
   * them downstream (combineLatest/withLatestFrom) surfaces transient mixes — a
   * foreign analyst's historyId while `isCurrentUser` still reads true, and the
   * inverse a tick later. `wireAnalystUrlSync` would then delete the param and
   * re-add it, forever.
   */
  private readonly desiredUrlHistoryId$ = this.analystStore.select(
    this.analystStore.selectedAnalystHistoryId$,
    this.analystStore.selectedAnalystIsCurrentUser$,
    (historyId, isCurrentUser): string | null => (isCurrentUser ? SELECTION_IS_SELF : historyId ?? null),
    { debounce: true }
  );

  public readonly currentUserAsAnalystOption$ = this.select(this.currentUser$, (user) =>
    user ? { id: user.id, name: (user.get('username') as string) ?? '', historyId: null, type: 'user' as const } : null
  );

  private readonly _detailCollapsed$ = new BehaviorSubject<boolean>(true);
  private readonly _protocolCollapsed$ = new BehaviorSubject<boolean>(false);
  private readonly _scale$ = new BehaviorSubject<number>(1);
  private readonly _showOverlay$ = new BehaviorSubject<boolean>(true);
  private readonly _showRoiDetails$ = new BehaviorSubject<boolean>(true);
  // Off on load: the crop itself is what the analyst reads, and the box over it is a
  // deliberate choice from the tile settings.
  private readonly _roiBoxVisible$ = new BehaviorSubject<boolean>(false);
  private readonly _editingEnabled$ = new BehaviorSubject<boolean>(true);

  private readonly SCALE_MIN = 1;
  private readonly SCALE_MAX = 2;

  public readonly detailCollapsed$ = this._detailCollapsed$.asObservable();
  public readonly protocolCollapsed$ = this._protocolCollapsed$.asObservable();
  public readonly scale$ = this._scale$.asObservable();
  public readonly showOverlay$ = this._showOverlay$.asObservable();
  public readonly showRoiDetails$ = this._showRoiDetails$.asObservable();
  public readonly roiBoxVisible$ = this._roiBoxVisible$.asObservable();
  public readonly editingEnabled$ = this._editingEnabled$.asObservable();

  public readonly panelIds$ = this.panelRegistry.panelIds$;
  public readonly maxPanelsReached$ = this.panelRegistry.maxPanelsReached$;
  public readonly syncing$ = this.roiChangeStore.syncing$;
  public readonly applyingChanges$ = this.roiChangeStore.applyingChanges$;
  public readonly revertingChanges$ = this.roiChangeStore.revertingChanges$;
  public readonly aiAnalysisInProgress$ = this.sampleAiProgressStore.inProgress$;

  public readonly aiAnalysisStatus$ = this.sampleAiProgressStore.status$;

  public readonly aiAnalysisViewOnly$ = combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$]).pipe(
    map(([status, selectedAnalyst]) => algorithmAiAnalysisLocked(status, selectedAnalyst)),
    distinctUntilChanged()
  );

  /**
   * Whether the rows on screen need a reload before they can be trusted again.
   *
   * Suppressed while changes are unsaved: locking the view also disables Save, and trapping
   * unsaved work behind a stale view is worse than showing it. Nothing is lost by waiting -
   * the store latches the requirement regardless, so it surfaces as soon as the save lands.
   */
  public readonly refreshRequired$ = combineLatest([
    this.roiStatsStore.viewRefreshRequired$,
    this.roiChangeStore.unsynced$,
  ]).pipe(
    map(([required, unsynced]) => required && !unsynced),
    distinctUntilChanged()
  );

  /** Suppressed alongside `refreshRequired$`, so the banner and its bar appear together. */
  public readonly projectionProgress$ = combineLatest([
    this.roiStatsStore.projectionProgress$,
    this.roiChangeStore.unsynced$,
  ]).pipe(
    map(([progress, unsynced]) => (unsynced ? undefined : progress)),
    distinctUntilChanged(
      (previous, current) => previous?.processed === current?.processed && previous?.total === current?.total
    )
  );

  public readonly projectionPollingExhausted$ = this.roiStatsStore.projectionPollingExhausted$;

  public readonly editingActive$ = combineLatest([
    this.editingEnabled$,
    this.syncing$,
    this.applyingChanges$,
    this.revertingChanges$,
    this.aiAnalysisViewOnly$,
    this.refreshRequired$,
  ]).pipe(
    map(
      ([editingEnabled, syncing, applyingChanges, revertingChanges, viewOnly, refreshRequired]) =>
        editingEnabled && !syncing && !applyingChanges && !revertingChanges && !viewOnly && !refreshRequired
    )
  );
  public readonly busy$ = combineLatest([
    this.syncing$,
    this.applyingChanges$,
    this.revertingChanges$,
  ]).pipe(
    map(([syncing, applying, reverting]) => syncing || applying || reverting)
  );
  public readonly updating$ = this.analysisStateStore.updating$;

  public readonly excludedFromCounter$ = this.roiStatsStore.counterExcludedLabels$;
  public readonly counterExclusionMode$ = this.roiStatsStore.counterExclusionMode$;
  public readonly statsMode$ = this.select(this.roiStatsStore.counterExclusionMode$, (mode) =>
    mode === 'selective' ? StatsMode.SELECTIVE : StatsMode.COMPLETE
  );

  public readonly labelCounts$ = this.roiStatsStore.labelCounts$;
  public readonly activeLabelCounts$ = this.roiStatsStore.activeLabelCounts$;
  public readonly hasUnlabeledRois$ = this.roiStatsStore.hasUnlabeledRois$;
  public readonly totalActiveDetections$ = this.roiStatsStore.totalActiveDetections$;
  public readonly totalDetections$ = this.roiStatsStore.totalDetections$;
  public readonly totalLabelCount$ = this.roiStatsStore.totalLabelCount$;
  public readonly statsLoading$ = this.roiStatsStore.loading$;
  public readonly loading$ = this.select(
    this.protocolLoading$,
    this.roiStore.roisLoading$,
    this.statsLoading$,
    this.roiChangeStore.changeRestorationComplete$,
    (protocolLoading, roisLoading, statsLoading, changeRestorationComplete) =>
      protocolLoading || roisLoading || statsLoading || !changeRestorationComplete
  );
  public readonly unsynced$ = this.roiChangeStore.unsynced$;
  public readonly synced$ = this.roiChangeStore.synced$;
  public readonly changes$ = this.roiChangeStore.changedRois$;
  public readonly changesRestored$ = this.roiChangeStore.changesRestored$;
  public readonly changeRestorationComplete$ = this.roiChangeStore.changeRestorationComplete$;

  public readonly tileSource$ = this.assetStore.tileSource$;
  public readonly selectedAssetId$ = this.assetStore.selectedAssetId$;
  public readonly changeCountPerLabel$ = this.roiChangeStore.changeCountPerLabel$;

  public readonly filterConfigsLoaded$ = this.filterConfigStore.loaded$;
  public readonly filterConfigsLoading$ = this.filterConfigStore.loading$;

  public readonly onSyncComplete$ = this.syncing$.pipe(
    pairwise(),
    concatLatestFrom(() => this.error$),
    map(([[prevSyncing, currentSyncing], error]) => prevSyncing && !currentSyncing && !error),
    filter((value) => !!value)
  );

  /**
   * A sync that landed, gated on the sync's OWN error. `error$` aggregates five stores, so a
   * single stale asset or protocol failure used to suppress the analysis-state promotion for
   * the rest of the session — findings saved, state left on READ.
   */
  public readonly onSyncSucceeded$ = this.syncing$.pipe(
    pairwise(),
    concatLatestFrom(() => this.roiChangeStore.error$),
    filter(([[prevSyncing, currentSyncing], syncError]) => prevSyncing && !currentSyncing && !syncError),
    map(() => undefined)
  );

  public readonly onUpdateComplete$ = this.updating$.pipe(
    pairwise(),
    concatLatestFrom(() => this.error$),
    map(([[prevUpdating, currentUpdating], error]) => prevUpdating && !currentUpdating && !error),
    filter((value) => !!value)
  );

  public readonly error$ = this.select(
    this.roiStore.error$,
    this.analystStore.error$,
    this.assetStore.error$,
    this.protocolStore.error$,
    this.analysisStateStore.error$,
    (mosaicError, analystError, assetError, protocolError, analysisStateError) =>
      mosaicError || analystError || assetError || protocolError || analysisStateError
  );

  public readonly sampleCategoryCounts$ = this.roiStatsEffects.sampleCategoryCounts$;

  public readonly categoryChangeCounts$ = this.select(
    this.roiChangeStore.changeCountPerLabel$,
    this.protocolStore.categories$,
    (changeCountPerLabel, categories) => {
      const categoryChanges = new Map<string, number>();

      for (const { id, options } of Object.values(categories)) {
        const count = options.reduce((acc, option) => acc + (changeCountPerLabel.get(option) ?? 0), 0);
        categoryChanges.set(id, count);
      }

      return categoryChanges;
    }
  );

  public readonly focusedPanelSelectedItems$ = this.panelRegistry.focusedPanelSelectedItems$;
  public readonly focusedPanelId$ = this.panelRegistry.focusedPanelId$;

  public readonly trackByPanelId = this.panelRegistry.trackByPanelId;

  // --- Effects ---

  public readonly toggleEditing = this.effect<boolean>((enabled$) =>
    enabled$.pipe(
      concatLatestFrom(() => [this.syncing$, this.applyingChanges$, this.revertingChanges$, this.aiAnalysisViewOnly$]),
      filter(([, syncing, applyingChanges, revertingChanges]) => !syncing && !applyingChanges && !revertingChanges),
      tap(([enabled, , , , viewOnly]) => {
        this._editingEnabled$.next(viewOnly ? false : enabled);
      })
    )
  );

  public readonly sync = this.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        this.hasUnlabeledRois$,
        this.editingEnabled$,
        this.sampleId$,
        this.selectedAnalyst$,
        this.selectedAnalystIsCurrentUser$,
        this.unsynced$,
        this.busy$,
      ]),
      tap(([, hasUnlabeledRois, editingEnabled, sampleId, selectedAnalyst, selectedAnalystIsCurrentUser, unsynced, busy]) => {
        if (!editingEnabled || !unsynced || busy) return;
        if (!selectedAnalystIsCurrentUser) {
          this.openReviewSampleDialog();
          return;
        }
        if (hasUnlabeledRois) {
          this.openUnlabeledDialog();
          return;
        }
        this.syncChanges(sampleId, selectedAnalyst.id);
      })
    )
  );

  constructor(
    private readonly globalStore: Store,
    private readonly roiStore: RoiStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiRestoreEffects: RoiRestoreEffects,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly roiAnalysisReviewEffects: RoiAnalysisReviewEffects,
    private readonly roiSyncEffects: RoiSyncEffects,
    private readonly roiStatsEffects: RoiStatsEffects,
    private readonly analystStore: AnalystStore,
    private readonly assetStore: AssetStore,
    private readonly panelRegistry: MosaicGalleryPanels,
    private readonly protocolStore: ProtocolStore,
    private readonly filterConfigStore: FilterConfigStore,
    private readonly filterConfigEffects: FilterConfigEffects,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly analysisStateStore: AnalysisStateStore,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService,
    private readonly roiRevertEffects: RoiRevertEffects,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    private readonly navigationStore: NavigationStore,
    private readonly breadcrumbNavigationEffects: BreadcrumbNavigationEffects,
    private readonly viewLoading: SampleViewLoadingEffects,
    private readonly viewContext: ViewContextStore,
    private readonly translate: TranslateService,
    @Inject(LOCALE_ID) private readonly locale: string
  ) {
    super({});

    this.protocolStore.selectSample(this.sampleId$);
    this.breadcrumbNavigationEffects.loadBreadcrumbOptions();
    this.wireBreadcrumbNavigationLoad();
    this.wireSampleAiProgressLoad();
    this.wireViewLocation();
    this.wireAnalystResolution();
    this.wireAnalystUrlSync();
    this.wireAnalystUrlPopstate();
    this.wireAiProgressDataRefresh();
    this.wireCounterExcludedToggle();
    this.wireAnalysisStateUpdatesOnSyncComplete();
    this.wireFocusedRoiAssetSelection();
    this.wireFilterConfigsLoading();
    this.wireReactiveEffects();
    this.wireAiAnalysisViewOnly();
    this.wireProjectionCheck();
  }


  /**
   * "Is the projection caught up NOW?" after a write. The measurement that follows a stats
   * read lives with the coordinator, keyed on the generation those stats belonged to.
   */
  private wireProjectionCheck(): void {
    // A save only: an AI tick already re-reads the counters, and the coordinator polls off
    // that read. Triggering here too restarted the poll twice per tick - two watermark reads
    // for one question, and a stall budget that could never run out while the AI ran.
    const afterWrite$ = this.onSyncComplete$.pipe(
      concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$]),
      filter(([, sampleId, analyst]) => !!sampleId && !!analyst),
      map(([, sampleId, analyst]) => ({ sampleId: sampleId as string, analyst: analyst as Analyst }))
    );

    this.roiStatsEffects.checkProjection(afterWrite$);
  }

  private wireAiAnalysisViewOnly(): void {
    this.aiAnalysisViewOnly$
      .pipe(
        filter((viewOnly) => viewOnly),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.toggleEditing(false));
  }

  private wireFilterConfigsLoading(): void {
    this.filterConfigEffects.loadConfigs(this.protocolStore.methodTypeId$.pipe(map(() => void 0)));
  }

  /**
   * Browser back/forward is the ONE case where the url is an input: the user moved
   * through history, so the selection must follow the url instead of the other way
   * round. Filtering on the `popstate` trigger is what keeps this edge unreachable
   * from our own writes — those are always `imperative` — so it cannot close a
   * url → store → url cycle.
   *
   * A pop that also changes the sample is already handled by
   * `wireAnalystResolution` (it reads the popped historyId off the snapshot); this
   * covers a query-param-only pop within one sample. Acting on NavigationEnd rather
   * than NavigationStart matters: `ExitEditorGuard` can cancel the navigation, and a
   * cancelled pop must not move the selection.
   *
   * The comparison against `desiredUrlHistoryId$` only skips redundant fetches — a
   * pop that leaves the analyst alone. It relies on that selector being warm, which
   * `wireAnalystUrlSync`'s lifetime subscription guarantees: `concatLatestFrom` drops
   * the emission if a debounced selector has not published yet.
   */
  private wireAnalystUrlPopstate(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart && e.navigationTrigger === 'popstate'),
        switchMap(() =>
          this.router.events.pipe(
            filter(
              (e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError
            ),
            take(1),
            filter((e): e is NavigationEnd => e instanceof NavigationEnd)
          )
        ),
        concatLatestFrom(() => [this.sampleId$, this.desiredUrlHistoryId$]),
        takeUntil(this.destroy$)
      )
      .subscribe(([, sampleId, desired]) => {
        if (!sampleId) return;

        // Live url rather than `route.snapshot`, for the same reason the sync uses it.
        const urlHistoryId = this.router.parseUrl(this.router.url).queryParams['historyId'] as string | undefined;
        const selected = desired === SELECTION_IS_SELF ? undefined : desired ?? undefined;
        if (urlHistoryId === selected) return;

        if (urlHistoryId) this.analystStore.getHistory(of(urlHistoryId));
        else this.analystStore.getCurrentAnalyst(of(sampleId));
      });
  }

  /**
   * Resolves which analyst to display whenever the location changes. The store's
   * `resolveAnalystForSample` prefers the URL's historyId (deep links / reloads),
   * then the analyst selected before the change — matched by analyst id, which is
   * stable across samples, unlike `historyId` — and only falls back to the current
   * user when that analyst has no analysis on the destination sample.
   *
   * Unlike the microscope viewer this needs no `ensureCurrentAnalystState` step:
   * the store's fallback branch get-or-creates the current user's analysis state
   * itself, the breadcrumb's "me" option comes from `currentUserAsAnalystOption$`
   * rather than the entity map, and analysis states load in
   * `wireAnalysisStatesLoad`.
   */
  private wireAnalystResolution(): void {
    combineLatest([this.sampleId$, this.caseId$])
      .pipe(
        filter(([sampleId, caseId]) => !!sampleId && !!caseId),
        distinctUntilChanged(([prevSampleId], [sampleId]) => prevSampleId === sampleId),
        takeUntil(this.destroy$)
      )
      .subscribe(([sampleId]) => {
        // Read from `route.snapshot` (not an observable) so we capture the value at
        // the moment of the call. A breadcrumb commit only sets this param when the
        // chosen analyst already has an analysis on the destination sample; otherwise
        // the value is null and `preferredAnalystId` below carries the intent.
        const preferredHistoryId = this.route.snapshot.queryParamMap.get('historyId') ?? undefined;
        // A breadcrumb commit names the analyst explicitly for exactly this sample; it
        // outranks both the url and the preserve rule. Consumed ONLY when the sample
        // matches — clearing it on a non-matching emission (an interleaved navigation, a
        // second emission of the same tick) silently downgraded the user's explicit pick
        // to preserve-or-me.
        const pending = this.pendingAnalystSelection;
        const preferredAnalystId = pending?.sampleId === sampleId ? pending.analystId : undefined;
        if (preferredAnalystId) this.pendingAnalystSelection = null;
        this.analystStore.resolveAnalystForSample(of({ sampleId, preferredHistoryId, preferredAnalystId }));
      });
  }

  /** Set by `openSampleAsAnalyst`, consumed by `wireAnalystResolution`. */
  private pendingAnalystSelection: { sampleId: string; analystId: string } | null = null;

  /**
   * Commit a breadcrumb cascade: open a sample AS a given analyst, in ONE navigation.
   *
   * Both halves of the change land together, so the view resolves once and fetches once.
   * Committing the sample first and the analyst after — which is what a sample-level
   * commit followed by an analyst pick would do — makes the mosaic load the new sample
   * for the outgoing analyst and then reload it, which is the flicker this replaces.
   */
  public readonly openSampleAsAnalyst = (caseId: string, sampleId: string, analystId: string) => {
    if (!caseId || !sampleId || !analystId) return;

    this.pendingAnalystSelection = { sampleId, analystId };

    // The historyId is only known when that analyst already has an analysis on the
    // destination sample; when they don't, `preferredAnalystId` carries the intent and
    // the store creates the state.
    const historyId = this.analystStore.analystsForSample(sampleId)?.find((a) => a.id === analystId)?.historyId;

    // PUSHES a history entry (no `replaceUrl`): this is a user navigation, so browser
    // Back must return to the sample they came from. Only `wireAnalystUrlSync`'s
    // reconciliation replaces, since it merely restates where the user already is.
    this.router.navigate([`/analyze/cases/${caseId}/mosaics/${sampleId}`], {
      queryParams: historyId ? { historyId } : {},
    });
  };

  /**
   * Mirrors the selected analyst into the url's `historyId` queryParam, so a reload
   * or a shared link reopens the same analysis (`resolveAnalystForSample` reads it
   * back as `preferredHistoryId`) and so a jump to the microscope viewer, which
   * merges query params, carries the selection over.
   *
   * The url is an OUTPUT here, never a source of truth for the selection: the store
   * is. Nothing reads this value back reactively — only `wireAnalystUrlPopstate`
   * treats the url as an input, and only for browser history navigation.
   *
   * Two invariants keep it safe: it compares against the LIVE url rather than the
   * router-store selector, which can still hold the pre-navigation value; and it
   * only ever rewrites the query param, never the path.
   */
  private wireAnalystUrlSync(): void {
    // Route params are inputs, not merely sampled: a case- or workspace-level
    // jump changes `caseid` and `sampleId`, and the resolution that
    // produced the current selection may land before they settle. Including them
    // means the sync re-evaluates once they do, instead of skipping for good and
    // leaving the URL without a historyId.
    combineLatest([this.desiredUrlHistoryId$, this.sampleId$, this.caseId$, this.analystStore.loading$])
      .pipe(
        switchMap((state) =>
          this.routerIdle$.pipe(
            filter((idle) => idle),
            take(1),
            map(() => state)
          )
        ),
        // `null` means nothing is resolved yet; writing then would clobber a
        // deep-link/reload URL's historyId before `resolveAnalystForSample` had a
        // chance to honour it. While `loading` is true the entity map can still hold
        // the previous sample's historyIds, so anything read now would be stale;
        // `loading` flipping false re-emits and syncs the settled value.
        filter(([desired, sampleId, caseId, loading]) => !!sampleId && !!caseId && desired !== null && !loading),
        takeUntil(this.destroy$)
      )
      .subscribe(([desired, sampleId, caseId]) => {
        // Adjust ONLY the query param of the URL the router is actually on; never
        // rebuild the path. A case-level jump changes `caseid` and `sampleId`, and a
        // path rebuilt from store state can name a half-updated (case, sample) pair —
        // navigating there re-triggers resolution, which syncs again, which
        // navigates again: the refresh loop. Bailing when the URL is not already the
        // resolved sample's mosaic costs nothing, since the next resolution emits
        // another sync.
        if (!this.router.url.split('?')[0].endsWith(`/analyze/cases/${caseId}/mosaics/${sampleId}`)) return;

        const urlTree = this.router.parseUrl(this.router.url);
        const desiredHistoryId = desired === SELECTION_IS_SELF ? undefined : desired;

        // Idempotence against the LIVE url, not the router-store selector above,
        // which can still hold the pre-navigation value. Without this a mismatch
        // between the two would let every emission navigate again.
        if ((urlTree.queryParams['historyId'] as string | undefined) === desiredHistoryId) return;

        urlTree.queryParams = { ...urlTree.queryParams };
        if (desiredHistoryId) urlTree.queryParams['historyId'] = desiredHistoryId;
        else delete urlTree.queryParams['historyId'];

        // A switch the user just asked for is a navigation and gets its own history
        // entry, so Back returns to the analyst they were reading. Everything else
        // reaching here is reconciliation — the url restating a selection that arrived
        // some other way — and must not add entries. Matched on the VALUE the user asked
        // for, so a reconciliation carrying some other value cannot consume the intent.
        const userInitiated = this.pendingUserAnalystChange === desired;
        if (userInitiated) this.pendingUserAnalystChange = null;
        this.router.navigateByUrl(urlTree, { replaceUrl: !userInitiated });
      });
  }

  /**
   * The url value the user just asked for, so the sync that carries it pushes a history
   * entry instead of replacing. Set by the analyst picks, which change the store and let
   * the sync mirror it — they have no navigation of their own to push. Holds the value
   * rather than a bare flag so only the matching sync consumes it.
   */
  private pendingUserAnalystChange: string | null = null;



  /**
   * While an algorithm analyst's execution is polling, the AI keeps producing
   * detections server-side. `status$` stays `inProgress` and won't re-emit on
   * its own. We use the store's throttled `dataRefreshTick$` to re-fetch the
   * sample stats and analysis states together, letting the counters update
   * live without a reload.
   */
  private wireAiProgressDataRefresh(): void {
    // A poll tick refreshes the generation already on screen — the coordinator re-reads for
    // the current context, so the counters advance without the grid starting over.
    const onAiPollTick$ = this.sampleAiProgressStore.dataRefreshTick$.pipe(map(() => void 0));

    this.viewLoading.refreshStats(onAiPollTick$);
    this.viewLoading.refreshAnalysisStates(onAiPollTick$);
  }

  private wireCounterExcludedToggle(): void {
    this.roiStatsEffects.getCounterExclusionConfig(this.protocolStore.methodTypeId$);
  }

  private wireFocusedRoiAssetSelection(): void {
    this.assetStore.selectAssetOnFocusedRoi(this.roiStore.focusedRoi$);
  }


  private wireAnalysisStateUpdatesOnSyncComplete(): void {
    this.analysisStateStore.updateCaseState(
      this.onSyncSucceeded$.pipe(
        concatLatestFrom(() => this.caseId$),
        filter(([, caseId]) => !!caseId),
        map(([, caseId]) => ({ state: IState.INPROGRES, currentCaseId: caseId }))
      )
    );

    this.analysisStateStore.updateSampleState(
      this.onSyncSucceeded$.pipe(
        concatLatestFrom(() => this.sampleId$),
        filter(([, sampleId]) => !!sampleId),
        map(([, sampleId]) => ({ sampleId, state: IState.INPROGRES }))
      )
    );
  }

  private wireReactiveEffects(): void {
    this.unsynced$.pipe(takeUntil(this.destroy$)).subscribe((unsynced) => (this.hasUnsyncedChanges = unsynced));

    const errors$ = this.error$.pipe(distinctUntilChanged(), takeUntil(this.destroy$));

    errors$
      .pipe(
        filter((value) => value === ApiAuthErrors.UNAUTHORIZED),
        take(1)
      )
      .subscribe(() => this.openUnauthorizedDialog());

    errors$
      .pipe(
        filter((value) => !!value && value !== ApiAuthErrors.UNAUTHORIZED),
        debounceTime(this.ALERT_DEBOUNCE_TIME)
      )
      .subscribe((error) => this.openSnackbar(error ?? 'error.unknown'));

    this.changesRestored$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        filter((value) => !!value),
        debounceTime(this.ALERT_DEBOUNCE_TIME)
      )
      .subscribe(() => this.openSnackbar('analysis.changes_restored'));

    merge(this.analystStore.analystChanged$, this.sampleId$.pipe(distinctUntilChanged()))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
          .pipe(take(1))
          .subscribe(([status, selectedAnalyst]) => {
            this.toggleEditing(!algorithmAiAnalysisLocked(status, selectedAnalyst));
          });
      });
  }

  public readonly navigateToCaseDetail = (caseId: string) => this.router.navigateByUrl(`/analyze/cases/${caseId}`);
  public readonly navigateToMicro = (caseId: string, sampleId: string) =>
    this.router.navigate([`/analyze/cases/${caseId}/images/${sampleId}`], { queryParamsHandling: 'merge' });
  public readonly navigateToMyMicroAnalysis = (caseId: string, sampleId: string) =>
    this.router.navigate([`/analyze/cases/${caseId}/images/${sampleId}`]);
  public readonly navigateToLogin = () => this.router.navigateByUrl('/login');
  public readonly removeLabels = (roiIds: string[], labels: string[]) =>
    this.roiLabelingEffects.removeLabels({ roiIds, labels });

  public readonly syncChanges = (sampleId: string, sourceAnalystId: string) => {
    combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
      .pipe(take(1))
      .subscribe(([status, selectedAnalyst]) => {
        if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
          this.saveBlockedByAiAnalysisSubject.next();
          return;
        }

        const currentAnalystId = Parse.User.current()?.id;
        if (!currentAnalystId) return;

        sourceAnalystId === currentAnalystId
          ? this.roiSyncEffects.syncChanges({ sampleId, currentAnalystId, sourceAnalystId })
          : this.roiAnalysisReviewEffects.reviewAnalysis({ sampleId, currentAnalystId, sourceAnalystId });
      });
  };

  /**
   * AI progress is per-generation: whether to poll depends on whether the analyst on screen
   * is an algorithm. Driven by the view context, which is only published once the analyst
   * has settled — so the ad hoc wait for `AnalystStore.loading` this used to carry is gone.
   */
  private wireSampleAiProgressLoad(): void {
    combineLatest([this.viewContext.context$, this.protocolStore.methodTypeId$])
      .pipe(
        map(([{ sampleId, analyst }, methodTypeId]) =>
          methodTypeId ? { sampleId, methodTypeId, poll: analyst.type === 'algorithm' } : null
        ),
        distinctUntilChanged(
          (a, b) => a?.sampleId === b?.sampleId && a?.methodTypeId === b?.methodTypeId && a?.poll === b?.poll
        ),
        takeUntil(this.destroy$),
        tap((params) => {
          this.sampleAiProgressStore.loadProgress(of(params));
        })
      )
      .subscribe();
  }

  /**
   * The breadcrumb's analyst pick. Drives the STORE; the url follows through
   * `wireAnalystUrlSync`. Selecting by navigating instead — the previous shape —
   * made the url an input as well as an output, and reactive reads plus reactive
   * writes of the same value are a cycle by construction.
   *
   * Selecting by analyst id needs no fetch: the dropdown's options come from the
   * entity map, which `resolveAnalystForSample` filled with this sample's analysts,
   * so their historyIds are the current sample's.
   */
  public readonly selectAnalyst = (analyst: Analyst) => {
    if (analyst.id === Parse.User.current()?.id) {
      this.selectOwnAnalysis();
      return;
    }
    this.pendingUserAnalystChange = analyst.historyId ?? null;
    this.analystStore.selectAnalyst(analyst.id);
  };

  /**
   * Switch to the current user's own analysis. Goes through `getCurrentAnalyst`
   * rather than the id-based updater because the analysis state for this sample may
   * not exist yet — that effect get-or-creates it.
   */
  public readonly selectOwnAnalysis = () => {
    this.pendingUserAnalystChange = SELECTION_IS_SELF;
    return this.analystStore.getCurrentAnalyst(
      this.sampleId$.pipe(
        filter((sampleId): sampleId is string => !!sampleId),
        take(1)
      )
    );
  };

  public readonly loadAnalysts = (sampleId: string) => this.analystStore.loadAnalysts(sampleId);
  public readonly analysisStates$ = this.analysisStateStore.analysisStates$;

  /**
   * The mosaic breadcrumb: the shared workspace/case/sample levels plus a
   * fourth level to switch the analyst whose analysis is displayed. Analysts
   * load lazily when their dropdown opens (see the component's `levelOpened`
   * handler), so until then the list only offers the current user.
   */
  public readonly breadcrumbLevels$: Observable<NavigationBreadcrumbLevel[]> = combineLatest([
    this.navigationStore.breadcrumbLevels$,
    this.selectedAnalyst$,
    this.selectedAnalystIsCurrentUser$,
    this.currentUserAsAnalystOption$,
    this.analysts$,
    this.analysisStates$,
    this.aiAnalysisStatus$,
    this.translate.stream('core.me'),
    this.analystStore.analystsBySampleId$,
  ]).pipe(
    map(
      ([
        navigationLevels,
        selectedAnalyst,
        isCurrentUser,
        meOption,
        analysts,
        analysisStates,
        aiStatus,
        meLabel,
        analystsBySampleId,
      ]) => [
          // The analyst is this view's deepest level, so every sample in the cascade
          // expands into it: picking an analyst there commits sample and analyst at once.
          ...navigationLevels.map((level) => ({
            ...level,
            options: this.withAnalystChildren(level.options, analystsBySampleId, meLabel),
          })),
          {
            key: 'analyst',
            labelKey: 'core.analyst',
            icon: 'ri-user-line',
            current: selectedAnalyst
              ? {
                id: selectedAnalyst.id,
                name: isCurrentUser ? meLabel : selectedAnalyst.name,
                indicator: MosaicDashboardFacade.toAiIndicator(selectedAnalyst, aiStatus),
              }
              : null,
            options: [
              ...(meOption ? [{ id: meOption.id, name: meLabel }] : []),
              ...(analysts ?? []).map((analyst) => this.toAnalystOption(analyst, analysisStates, aiStatus)),
            ],
          },
        ]
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Walks the navigation tree and turns every sample node into an expandable one whose
   * children are that sample's analysts.
   *
   * "Me" is always offered first, even for a sample nobody has analysed — the fetch only
   * returns analysts that already have an analysis state, so without it such a sample
   * would expand into an empty menu and could not be opened at all.
   *
   * Cascaded analyst rows carry no last-updated hint: that comes from
   * `AnalysisStateStore`, which is scoped to the open sample.
   */
  private withAnalystChildren(
    options: NavigationBreadcrumbOption[] | null | undefined,
    analystsBySampleId: Record<string, Analyst[] | null>,
    meLabel: string
  ): NavigationBreadcrumbOption[] | null {
    if (!options) return options ?? null;

    return options.map((option) => {
      if (option.levelKey !== 'sample') {
        return { ...option, children: this.withAnalystChildren(option.children, analystsBySampleId, meLabel) };
      }

      const analysts = analystsBySampleId[option.id];
      const currentUserId = Parse.User.current()?.id;
      const children = analysts
        ? [
          ...(currentUserId ? [{ id: currentUserId, name: meLabel, levelKey: 'analyst' }] : []),
          ...analysts
            .filter((analyst) => analyst.id !== currentUserId)
            .map((analyst) => ({
              id: analyst.id,
              name: analyst.name,
              levelKey: 'analyst',
              badge: analyst.type === 'algorithm' ? 'AI' : undefined,
            })),
        ]
        : null;

      return { ...option, expandable: true, children };
    });
  }

  private toAnalystOption(
    analyst: Analyst,
    analysisStates: Map<string, { updatedAt?: Date }> | null,
    aiStatus: string | undefined
  ): NavigationBreadcrumbOption {
    const updatedAt = analysisStates?.get(analyst.id)?.updatedAt;
    return {
      id: analyst.id,
      name: analyst.name,
      badge: analyst.type === 'algorithm' ? 'AI' : undefined,
      hint: updatedAt ? formatDate(updatedAt, 'dd/MM/yyyy, hh:mm a', this.locale) : undefined,
      indicator: MosaicDashboardFacade.toAiIndicator(analyst, aiStatus),
    };
  }

  /** AI-progress dot for algorithm analysts: green once the analysis is ready,
   *  orange while it runs — same semantics the old mat-select indicator had. */
  private static toAiIndicator(
    analyst: Analyst,
    aiStatus: string | undefined
  ): NavigationBreadcrumbIndicator | undefined {
    if (analyst.type !== 'algorithm' || !aiStatus) return undefined;
    return {
      kind: aiStatus === 'ready' ? 'success' : 'in-progress',
      tooltip: aiStatus === 'ready' ? 'analyzed' : aiStatus,
    };
  }

  private wireBreadcrumbNavigationLoad(): void {
    this.sampleId$
      .pipe(
        filter((sampleId): sampleId is string => !!sampleId),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((sampleId) => this.breadcrumbNavigationEffects.fetchNavigationReferences(sampleId));
  }


  /** Cascading breadcrumb: load the level below an option the user just expanded. */
  public readonly loadCasesForWorkspace = (workspaceId: string) =>
    this.breadcrumbNavigationEffects.loadCasesForWorkspace(of(workspaceId));
  public readonly loadSamplesForCase = (caseId: string) =>
    this.breadcrumbNavigationEffects.loadSamplesForCase(of(caseId));
  public readonly loadAnalystsForSample = (sampleId: string) =>
    this.analystStore.loadAnalystsForSampleId(of(sampleId));

  public readonly loadAnalysisStates = (sampleId: string, caseId: string) =>
    this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });

  /**
   * Tells the loading coordinator where the view is. It works out which analyst is
   * actually resolved for that location, publishes the pair as the view context, and drives
   * the per-sample reads from it — so no read has to guess the pair for itself.
   */
  private wireViewLocation(): void {
    combineLatest([this.sampleId$, this.caseId$])
      .pipe(
        filter(([sampleId, caseId]) => !!sampleId && !!caseId),
        distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
        takeUntil(this.destroy$)
      )
      .subscribe(([sampleId, caseId]) => this.viewLoading.openLocation(of({ caseId, sampleId })));
  }

  public readonly setStatsMode = (mode: StatsMode) => {
    this.roiStatsEffects.toggleCounterExclusionMode(mode === StatsMode.SELECTIVE ? 'selective' : 'complete');
  };

  // --- Dialog methods (moved from component) ---

  public openUnauthorizedDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'error.session_expired',
        acceptButtonText: 'button.login',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.navigateToLogin());
  }

  public openReviewSampleDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.review_sample',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.continue',
      },
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !(result !== 'accept' && result !== 'default')),
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$]),
        switchMap(([, sampleId, selectedAnalyst]) => {
          this.syncChanges(sampleId, selectedAnalyst.id);
          return combineLatest([this.onSyncComplete$, this.onUpdateComplete$]).pipe(take(1));
        })
      )
      // The reviewed analysis has been merged into our own, so show ours. Store-driven:
      // `wireAnalystUrlSync` clears the url's historyId to match.
      .subscribe(() => this.selectOwnAnalysis());
  }

  public acceptCompleteAnalysis(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.accept_complete_analysis',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.accept_complete_analysis',
      },
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !(result !== 'accept' && result !== 'default')),
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$]),
        switchMap(([, sampleId, selectedAnalyst]) => {
          this.syncChanges(sampleId, selectedAnalyst.id);
          return combineLatest([this.onSyncComplete$, this.onUpdateComplete$]).pipe(take(1));
        })
      )
      // The reviewed analysis has been merged into our own, so show ours. Store-driven:
      // `wireAnalystUrlSync` clears the url's historyId to match.
      .subscribe(() => this.selectOwnAnalysis());
  }

  public openDiscardChangesDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.discard_changes_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.discard_changes',
        defaultAction: 'cancel',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result === 'accept' || result === 'default'),
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$])
      )
      .subscribe(([, sampleId, analyst]) =>
        this.roiRevertEffects.revertAllChanges({ sampleId, creatorId: analyst.id })
      );
  }

  public openUnlabeledDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.change_mode_with_unlabeled_regions',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.continue',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => !(result !== 'accept' && result !== 'default')),
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$])
      )
      .subscribe(([, sampleId, selectedAnalyst]) => this.syncChanges(sampleId, selectedAnalyst.id));
  }

  public openExitMosaicDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.exit_mosaic_mode',
        cancelButtonText: 'button.cancel',
        dontSaveButtonText: 'button.exit',
        acceptButtonText: 'button.review',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    const onDialogClose = this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((answer) => answer !== 'cancel')
      );

    onDialogClose // on discard
      .pipe(
        filter((answer) => answer === 'discard'),
        concatLatestFrom(() => this.caseId$)
      )
      .subscribe(([, caseId]) => this.navigateToCaseDetail(caseId));

    onDialogClose // on accept
      .pipe(
        filter((answer) => answer === 'accept'),
        concatLatestFrom(() => [this.caseId$, this.sampleId$])
      )
      .subscribe(([, caseId, sampleId]) => this.navigateToMicro(caseId, sampleId));
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.exit_without_saving_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.save',
        dontSaveButtonText: 'button.continue',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    const onDialogClose = this.dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((answer) => answer !== 'cancel')
      );

    onDialogClose // on discard
      .pipe(
        filter((answer: string) => answer === 'discard'),
        concatLatestFrom(() => this.caseId$)
      )
      .subscribe(([, caseId]) => this.navigateToCaseDetail(caseId));

    onDialogClose // on accept
      .pipe(filter((answer: string) => answer === 'accept'))
      .subscribe(() => this.sync());
  }

  // --- Sync and exit methods (moved from component) ---

  public exit(): void {
    combineLatest([this.unsynced$, this.aiAnalysisInProgress$, this.caseId$])
      .pipe(take(1))
      .subscribe(([unsynced, aiInProgress, caseId]) => {
        if (unsynced) {
          this.openUnsavedChangesDialog();
          return;
        }
        if (aiInProgress) {
          this.navigateToCaseDetail(caseId);
          return;
        }
        this.openExitMosaicDialog();
      });
  }

  public canDeactivate(): Observable<boolean> {
    return this.busy$.pipe(
      take(1),
      tap((busy) => {
        if (busy) this.snackbarService.show('info.wait_for_changes_to_finish');
      }),
      map((busy) => !busy)
    );
  }

  // --- Snackbar methods (moved from component) ---

  public openSnackbar(message: string): void {
    this.snackbarService.show(message);
  }

  // --- Label methods (moved from component) ---

  public addLabelToFocusedItems(label: string): void {
    this.panelRegistry.addLabelsToFocusedItems([label]);
    this.roiStore.clearFocusedRoi();
  }

  public onLabelSelected(labelName: string, metaKeyPressed: boolean): void {
    if (metaKeyPressed) {
      this.openLabelInNewPanel(labelName);
    } else {
      this.openLabelInFocusedPanel(labelName);
    }
  }

  public openLabelInFocusedPanel(labelName: string): void {
    this.panelRegistry.filterFocusedPanelByLabel(labelName);
  }

  public openLabelInNewPanel(labelName: string): void {
    this.panelRegistry.openNewPanelWithLabel(labelName);
  }

  public openSpecialInFocusedPanel(kind: 'unlabeled' | 'changed' | 'selected'): void {
    switch (kind) {
      case 'unlabeled':
        this.panelRegistry.filterFocusedPanelByUnlabeled();
        return;
      case 'changed':
        this.panelRegistry.filterFocusedPanelByChanged();
        return;
      case 'selected':
        this.panelRegistry.filterFocusedPanelBySelected();
        return;
    }
  }

  public openSpecialInNewPanel(kind: 'unlabeled' | 'changed' | 'selected'): void {
    switch (kind) {
      case 'unlabeled':
        this.panelRegistry.openNewPanelWithUnlabeled();
        return;
      case 'changed':
        this.panelRegistry.openNewPanelWithChanged();
        return;
      case 'selected':
        this.panelRegistry.openNewPanelWithSelected();
        return;
    }
  }

  public onSpecialSelected(kind: 'unlabeled' | 'changed' | 'selected', metaKeyPressed: boolean): void {
    if (metaKeyPressed) {
      this.openSpecialInNewPanel(kind);
    } else {
      this.openSpecialInFocusedPanel(kind);
    }
  }

  // --- Layout methods ---

  public toggleProtocol(): void {
    this._protocolCollapsed$.next(!this._protocolCollapsed$.value);
  }

  public toggleDetail(): void {
    this._detailCollapsed$.next(!this._detailCollapsed$.value);
  }

  public openDetail(): void {
    this._detailCollapsed$.next(false);
  }

  public toggleOverlay(): void {
    this._showOverlay$.next(!this._showOverlay$.value);
  }

  public toggleRoiDetails(): void {
    this._showRoiDetails$.next(!this._showRoiDetails$.value);
  }

  public setRoiBoxVisible(visible: boolean): void {
    this._roiBoxVisible$.next(visible);
  }

  public setTileSize(size: number): void {
    const clamped = Math.max(this.SCALE_MIN, Math.min(this.SCALE_MAX, size));
    this._scale$.next(clamped);
  }

  // --- ROI pass-through methods ---

  public clearFocusedRoi(): void {
    this.roiStore.clearFocusedRoi();
  }

  public setFocusedRoi(id: string): void {
    this.roiStore.setFocusedRoi(id);
  }

  public toggleCounterExcludedLabel(id: string): void {
    this.roiStatsEffects.toggleCounterExcluded(id);
  }

  // --- Panel pass-through methods ---

  public addPanel(): void {
    this.panelRegistry.addPanel();
  }

  public focusNextPanel(): void {
    this.panelRegistry.focusNextPanel();
  }

  public focusPreviousPanel(): void {
    this.panelRegistry.focusPreviousPanel();
  }

  public focusPanelByOrder(order: number): void {
    this.panelRegistry.focusPanelByOrder(order);
  }

  public removePanel(id: number): void {
    this.panelRegistry.removePanel(id);
  }

  public removeAllLabelsOnFocusedItems(): void {
    this.panelRegistry.removeAllLabelsOnFocusedItems();
  }

  public filterFocusedPanelByAsset(assetId: string): void {
    this.panelRegistry.filterFocusedPanelByAsset(assetId);
  }

  public filterFocusedPanelBySelected(): void {
    this.panelRegistry.filterFocusedPanelBySelected();
  }

  public addLabelsToFocusedItems(labels: string[]): void {
    this.panelRegistry.addLabelsToFocusedItems(labels);
  }

  // --- Stats pass-through methods ---

  public toggleCounterExclusionMode(mode: string): void {
    this.roiStatsEffects.toggleCounterExclusionMode(mode as CounterExclusionMode);
  }
}
